// How We Do It / Elakay Academy -- builds the document rendered inside the
// isolated preview/public frame (HowWeDoItSandboxedFrame).
//
// Security model (unchanged): the tutorial page runs in an
// `<iframe sandbox="allow-scripts ...">` WITHOUT allow-same-origin, so its
// own JavaScript can never reach Elakay's origin (tokens, cookies, storage).
//
// What this builder adds around the author's page:
//  1. a Content-Security-Policy (external styles/scripts/fonts only from the
//     allow-listed CDNs, no network calls, no nested frames, no form posts);
//  2. a low-specificity responsive safety layer (author CSS always wins);
//  3. YouTube/Vimeo players: inside an opaque-origin sandbox the players
//     refuse to start (verified: black frame / "Error 153"), so each player
//     <iframe> is replaced by a sized placeholder ("slot") and the PARENT
//     renders the real player exactly over it (see HowWeDoItSandboxedFrame);
//  4. stored How We Do It media paths (/data/<folder>/how-we-do-it/...) are
//     pointed at the API route production actually serves;
//  5. a small shim reporting the content height (auto-height, no double
//     scroll), the player slots, clicked links (opened by the parent) and
//     media errors. The author's stored source is never modified: all of
//     this happens at render time only.
import { buildTutorialHtmlDocument, serializeHowWeDoItPage } from "./howWeDoItPageDocument";
import { hasTutorialHtmlCode } from "./howWeDoItRenderMode";
import { parseVideoUrl, isValidVideoRef, buildVideoThumbnailUrl, buildVideoWatchUrl } from "./howWeDoItVideoUrl";
import { resolveHowWeDoItMediaUrl } from "./howWeDoItMediaUrl";
import { ALLOWED_STYLE_HOSTS, ALLOWED_SCRIPT_HOSTS, escapeHtml } from "./howWeDoItContentFormats";
import { LAYOUT_DEVICES, buildLayoutCss } from "./blockLayout";
import { buildDesignerBlocks, buildDesignerScript } from "./howWeDoItDesigner";

export const FRAME_MESSAGE_SOURCE = "elakay-hwdi-sandbox";
export const PARENT_MESSAGE_SOURCE = "elakay-hwdi-parent";

const HWDI_MEDIA_PATH = /\/data\/(?:images|videos|files)\/how-we-do-it\/[^?#\s"']+/i;

const originOf = (value) => {
  try {
    const base = typeof window !== "undefined" ? window.location.href : "http://localhost/";
    const url = new URL(String(value), base);
    return /^https?:$/.test(url.protocol) ? url.origin : "";
  } catch {
    return "";
  }
};

const unique = (values) => [...new Set(values.filter(Boolean))];

/** Content-Security-Policy of the frame document. */
export const buildFrameCsp = ({ appOrigin = "", apiOrigin = "", extraStyleOrigins = [] } = {}) => {
  const own = unique([appOrigin, apiOrigin]);
  const https = (hosts) => hosts.map((host) => `https://${host}`);
  const directives = [
    "default-src 'none'",
    `script-src 'unsafe-inline' 'unsafe-eval' ${https(ALLOWED_SCRIPT_HOSTS).join(" ")}`,
    `style-src 'unsafe-inline' ${unique([...own, ...extraStyleOrigins, ...https(ALLOWED_STYLE_HOSTS)]).join(" ")}`,
    // Web fonts carry no script: any https source (the site's own CSS uses
    // several font CDNs) plus Elakay's origins.
    `font-src data: https: ${own.join(" ")}`.trim(),
    `img-src data: blob: https: ${own.join(" ")}`.trim(),
    `media-src data: blob: https: ${own.join(" ")}`.trim(),
    "frame-src 'none'",
    "connect-src 'none'",
    "form-action 'none'",
    "base-uri 'none'",
    "object-src 'none'",
    "worker-src 'none'",
    "manifest-src 'none'",
  ];
  return directives.join("; ");
};

// Low specificity (:where = 0) so any author rule wins; only fills gaps that
// make pasted pages overflow on phones. Slot/notice styles are Elakay's own.
export const FRAME_BASE_CSS = `
:where(html){-webkit-text-size-adjust:100%;text-size-adjust:100%}
:where(body){overflow-wrap:break-word}
:where(img,video,canvas,svg){max-width:100%}
:where(img,video){height:auto}
:where(table){max-width:100%}
:where(pre){max-width:100%;overflow:auto}
.hwdi-video-slot{position:relative;display:block;width:100%;aspect-ratio:16/9;background:#0b1512;border-radius:inherit;overflow:hidden;color:#fff;font:600 14px/1.3 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif}
.hwdi-video-frame>.hwdi-video-slot,.hwdi-md-video>.hwdi-video-slot{position:absolute;inset:0;width:100%;height:100%;aspect-ratio:auto}
.hwdi-md-video{position:relative;width:100%;aspect-ratio:16/9;margin:1em 0;border-radius:12px;overflow:hidden}
.hwdi-video-slot img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;opacity:.85}
.hwdi-video-slot__label{position:absolute;left:12px;right:12px;bottom:12px;display:flex;align-items:center;gap:8px;text-shadow:0 1px 3px rgba(0,0,0,.6)}
.hwdi-video-slot__play{flex:none;display:inline-flex;align-items:center;justify-content:center;width:44px;height:44px;border-radius:50%;background:rgba(13,127,91,.92)}
.hwdi-video-slot__play::before{content:"";margin-left:4px;border-style:solid;border-width:9px 0 9px 15px;border-color:transparent transparent transparent #fff}
.hwdi-video-slot a{color:#fff}
.hwdi-embed-notice,.hwdi-media-error{display:block;box-sizing:border-box;width:100%;padding:14px 16px;margin:8px 0;border:1px dashed #cad7d1;border-radius:12px;background:#f4f7f5;color:#3d534c;font:500 14px/1.45 system-ui,-apple-system,"Segoe UI",Roboto,sans-serif}
.hwdi-media-error a,.hwdi-embed-notice a{color:#0a6a4c;font-weight:700}
.hwdi-prose{max-width:860px;margin:0 auto;padding:8px 4px 32px;color:#14231f;font-size:16px;line-height:1.7}
.hwdi-prose h1,.hwdi-prose h2,.hwdi-prose h3,.hwdi-prose h4{line-height:1.25;margin:1.4em 0 .5em;color:#14231f}
.hwdi-prose a{color:#0a6a4c}
.hwdi-prose blockquote{margin:1em 0;padding:.6em 1em;border-left:4px solid #0d7f5b;background:#e6f8ee;border-radius:0 10px 10px 0}
.hwdi-prose pre{background:#0d1b16;color:#d9f5e6;padding:14px;border-radius:10px}
.hwdi-prose code{font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:.92em}
.hwdi-prose table{border-collapse:collapse;width:100%}
.hwdi-prose th,.hwdi-prose td{border:1px solid #cad7d1;padding:8px 10px;text-align:left}
.hwdi-prose .hwdi-table-scroll{overflow-x:auto}
`;

// ---------------------------------------------------------------------------
// Render-time markup preparation (players -> slots, media paths)
// ---------------------------------------------------------------------------

const VIDEO_FLAG = (url, name) => {
  try {
    return new URL(url).searchParams.get(name);
  } catch {
    return null;
  }
};

const slotDimensionsStyle = (frame) => {
  const width = Number(frame.getAttribute("width"));
  const height = Number(frame.getAttribute("height"));
  if (width > 0 && height > 0) return `max-width:${width}px;aspect-ratio:${width}/${height};`;
  return "";
};

const buildSlot = (doc, frame, ref, index, labels) => {
  const slot = doc.createElement("div");
  const src = frame.getAttribute("src") || "";
  slot.className = `hwdi-video-slot ${frame.getAttribute("class") || ""}`.trim();
  slot.setAttribute("data-hwdi-video-slot", `v${index}-${ref.provider}-${ref.id}`);
  slot.setAttribute("data-provider", ref.provider);
  slot.setAttribute("data-id", ref.id);
  if (ref.hash) slot.setAttribute("data-hash", ref.hash);
  if (ref.start) slot.setAttribute("data-start", String(ref.start));
  ["autoplay", "controls"].forEach((name) => {
    const value = VIDEO_FLAG(src, name);
    if (value !== null) slot.setAttribute(`data-${name}`, value === "1" || value === "true" ? "1" : "0");
  });
  const mute = VIDEO_FLAG(src, "mute") ?? VIDEO_FLAG(src, "muted");
  if (mute !== null) slot.setAttribute("data-muted", mute === "1" || mute === "true" ? "1" : "0");
  const title = frame.getAttribute("title") || "";
  if (title) slot.setAttribute("data-title", title);
  slot.setAttribute("style", `${slotDimensionsStyle(frame)}${frame.getAttribute("style") || ""}`);
  ["data-elakay-field", "data-elakay-video-source", "data-elakay-source-url", "data-elakay-rendered-url", "id"].forEach((name) => {
    if (frame.hasAttribute(name)) slot.setAttribute(name, frame.getAttribute(name));
  });
  const thumbnail = buildVideoThumbnailUrl(ref);
  const watchUrl = buildVideoWatchUrl(ref);
  slot.innerHTML =
    (thumbnail ? `<img src="${escapeHtml(thumbnail)}" alt="" loading="lazy" />` : "") +
    `<span class="hwdi-video-slot__label"><span class="hwdi-video-slot__play"></span>` +
    `<a href="${escapeHtml(watchUrl)}" target="_blank" rel="noopener noreferrer">${escapeHtml(title || labels.watchVideo)}</a></span>`;
  return slot;
};

const buildNotice = (doc, className, text, href = "") => {
  const notice = doc.createElement("div");
  notice.className = className;
  notice.textContent = text;
  if (href && /^https?:\/\//i.test(href)) {
    notice.appendChild(doc.createTextNode(" "));
    const link = doc.createElement("a");
    link.href = href;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    link.textContent = new URL(href).hostname;
    notice.appendChild(link);
  }
  return notice;
};

const rewriteMediaAttribute = (element, attribute) => {
  const value = element.getAttribute(attribute);
  if (value && HWDI_MEDIA_PATH.test(value)) {
    const resolved = resolveHowWeDoItMediaUrl(value);
    if (resolved && resolved !== value) element.setAttribute(attribute, resolved);
  }
};

/**
 * Replaces YouTube/Vimeo iframes by player slots, other iframes by a
 * "blocked" notice, and rebases stored How We Do It media paths.
 * @returns {{html: string, slots: number, blocked: string[]}}
 */
export const prepareSandboxMarkup = (html, labels = DEFAULT_LABELS) => {
  const source = String(html || "");
  if (typeof DOMParser === "undefined" || (!/<iframe/i.test(source) && !HWDI_MEDIA_PATH.test(source))) {
    return { html: source, slots: 0, blocked: [] };
  }
  const doc = new DOMParser().parseFromString(source, "text/html");
  let slots = 0;
  const blocked = [];
  [...doc.querySelectorAll("iframe")].forEach((frame) => {
    const src = frame.getAttribute("src") || "";
    const ref = parseVideoUrl(src);
    if (ref && isValidVideoRef(ref)) {
      slots += 1;
      frame.replaceWith(buildSlot(doc, frame, ref, slots, labels));
    } else if (ref) {
      frame.replaceWith(buildNotice(doc, "hwdi-media-error", labels.videoUnavailable, /^https?:\/\//i.test(src) ? src : ""));
    } else {
      blocked.push(src);
      frame.replaceWith(buildNotice(doc, "hwdi-embed-notice", labels.embedBlocked, src));
    }
  });
  doc.querySelectorAll("img[src], source[src], video[src], audio[src]").forEach((element) => rewriteMediaAttribute(element, "src"));
  doc.querySelectorAll("video[poster]").forEach((element) => rewriteMediaAttribute(element, "poster"));
  doc.querySelectorAll("a[href]").forEach((element) => rewriteMediaAttribute(element, "href"));
  doc.querySelectorAll("video").forEach((video) => {
    if (!video.hasAttribute("playsinline")) video.setAttribute("playsinline", "");
  });
  return { html: `<!doctype html>\n${doc.documentElement.outerHTML}`, slots, blocked };
};

// ---------------------------------------------------------------------------
// Shim (runs inside the frame only)
// ---------------------------------------------------------------------------

export const DEFAULT_LABELS = Object.freeze({
  watchVideo: "Watch the video",
  videoUnavailable: "This video is unavailable (unrecognized address).",
  embedBlocked: "Embedded content blocked: this source is not allowed.",
  mediaError: "This media could not be loaded.",
  openOriginal: "Open",
});

const buildShimScript = (labels) => {
  const config = JSON.stringify({ source: FRAME_MESSAGE_SOURCE, parent: PARENT_MESSAGE_SOURCE, labels }).replace(/</g, "\\u003c");
  return `<script data-hwdi-frame-shim>(function(){
var C=${config};
function post(kind,data){try{parent.postMessage({source:C.source,kind:kind,data:data},"*");}catch(e){}}
function postConsole(kind,args){post(kind,{args:Array.prototype.map.call(args,function(a){try{return typeof a==="string"?a:JSON.stringify(a);}catch(e){return String(a);}})});}
["log","warn","error","info"].forEach(function(m){var o=console[m];console[m]=function(){postConsole(m,arguments);if(o)o.apply(console,arguments);};});
window.addEventListener("error",function(e){var t=e.target;if(t&&t!==window&&t.tagName){if(t.tagName==="VIDEO"||t.tagName==="SOURCE"){mediaError(t.tagName==="SOURCE"?t.parentNode:t);}return;}postConsole("error",[e.message+" ("+e.filename+":"+e.lineno+")"]);},true);
window.addEventListener("unhandledrejection",function(e){postConsole("error",["Unhandled promise rejection: "+(e.reason&&e.reason.message||e.reason)]);});
function mediaError(v){if(!v||v.getAttribute("data-hwdi-error"))return;v.setAttribute("data-hwdi-error","1");var d=document.createElement("div");d.className="hwdi-media-error";d.textContent=C.labels.mediaError+" ";var s=v.currentSrc||v.getAttribute("src")||"";if(/^https?:/i.test(s)){var a=document.createElement("a");a.href=s;a.target="_blank";a.rel="noopener noreferrer";a.textContent=C.labels.openOriginal;d.appendChild(a);}v.style.display="none";v.parentNode&&v.parentNode.insertBefore(d,v.nextSibling);schedule();}
var last="",pending=false,grow=[],frozen=false,freezeH=0;
function height(){var de=document.documentElement,b=document.body;var h=Math.ceil(de.getBoundingClientRect().height);if(b){h=Math.max(h,Math.ceil(b.scrollHeight));}return h;}
function slots(){var out=[];var list=document.querySelectorAll("[data-hwdi-video-slot]");for(var i=0;i<list.length&&i<60;i++){var el=list[i];var r=el.getBoundingClientRect();var cs=getComputedStyle(el);out.push({key:el.getAttribute("data-hwdi-video-slot"),provider:el.getAttribute("data-provider"),id:el.getAttribute("data-id"),hash:el.getAttribute("data-hash")||"",start:Number(el.getAttribute("data-start"))||0,autoplay:el.getAttribute("data-autoplay")==="1",muted:el.getAttribute("data-muted")==="1",controls:el.getAttribute("data-controls")!=="0",title:el.getAttribute("data-title")||"",x:r.left,y:r.top,w:r.width,h:r.height,visible:r.width>1&&r.height>1&&cs.visibility!=="hidden"&&cs.display!=="none"});}return out;}
function report(){pending=false;var h=height();if(!frozen){var now=Date.now();grow=grow.filter(function(g){return now-g.t<1500;});if(grow.length&&h>grow[grow.length-1].h){grow.push({t:now,h:h});}else{grow=[{t:now,h:h}];}if(grow.length>12){frozen=true;freezeH=grow[0].h;}}if(frozen){h=freezeH;}var payload={height:h,frozen:frozen,slots:slots(),scrollY:window.scrollY||0};var key=JSON.stringify(payload);if(key===last)return;last=key;post("layout",payload);}
function schedule(){if(pending)return;pending=true;(window.requestAnimationFrame||setTimeout)(report);}
if(window.ResizeObserver){var ro=new ResizeObserver(schedule);ro.observe(document.documentElement);var watchBody=function(){if(document.body)ro.observe(document.body);};if(document.body)watchBody();else document.addEventListener("DOMContentLoaded",watchBody);}
if(window.MutationObserver){new MutationObserver(schedule).observe(document.documentElement,{subtree:true,childList:true,attributes:true,characterData:true});}
window.addEventListener("load",schedule);window.addEventListener("resize",schedule);window.addEventListener("scroll",schedule,true);document.addEventListener("load",schedule,true);document.addEventListener("toggle",schedule,true);document.addEventListener("transitionend",schedule,true);
if(document.fonts&&document.fonts.ready){document.fonts.ready.then(schedule);}
var ticks=0;var timer=setInterval(function(){schedule();if(++ticks>10)clearInterval(timer);},400);
document.addEventListener("DOMContentLoaded",schedule);schedule();
window.addEventListener("click",function(e){if(e.defaultPrevented||e.button>1)return;var a=e.target&&e.target.closest?e.target.closest("a[href]"):null;if(!a)return;var raw=a.getAttribute("href")||"";if(/^\\s*javascript:/i.test(raw))return;if(raw.charAt(0)==="#"){e.preventDefault();var id=decodeURIComponent(raw.slice(1));var t=id?(document.getElementById(id)||document.getElementsByName(id)[0]):document.body;if(t){post("scrollTo",{y:t.getBoundingClientRect().top});}return;}var href;try{href=new URL(raw,document.baseURI).href;}catch(x){return;}if(!/^(https?|mailto|tel):/i.test(href))return;e.preventDefault();post("navigate",{href:href,newTab:a.target==="_blank"||e.ctrlKey||e.metaKey||e.shiftKey||e.button===1||a.hasAttribute("download"),download:a.hasAttribute("download")});},false);
window.addEventListener("message",function(e){if(!e.data||e.data.source!==C.parent)return;if(e.data.action==="scrollToBlock"){var el=document.getElementById("hwdi-block-"+e.data.blockId);if(el)post("scrollTo",{y:el.getBoundingClientRect().top,reveal:!!e.data.reveal});}});
})();</script>`;
};

// ---------------------------------------------------------------------------
// Document assembly
// ---------------------------------------------------------------------------

// Elakay's own stylesheets are added for content written FOR Elakay
// (contentBlocks, fragments, Markdown/plain text, pages exported from the
// visual blocks) -- never forced onto a complete standalone page, whose own
// CSS must not be altered by the site's global rules (the root marker alone
// does not opt in: an Academy page carries it with its own design). A page
// can opt in with <html data-elakay-app-styles>.
export const shouldUseAppStyles = (tutorial) => {
  if (!hasTutorialHtmlCode(tutorial)) return true;
  const code = String(tutorial.htmlCode || "");
  if (!/<html[\s>]/i.test(code)) return true;
  return /data-elakay-block-id|data-elakay-app-styles|class=["'][^"']*\bhwdi-/i.test(code);
};

const injectAfterOpeningTag = (page, tag, markup) => {
  const pattern = new RegExp(`<${tag}(\\s[^>]*)?>`, "i");
  if (pattern.test(page)) return page.replace(pattern, (match) => `${match}\n${markup}\n`);
  return null;
};

/**
 * The complete srcdoc for a tutorial.
 * @param {object} tutorial  canonical tutorial (or Console draft)
 * @param {object} options   { appStylesMarkup, appOrigin, apiOrigin, styleOrigins, labels }
 */
/**
 * options.device ("desktop" | "tablet" | "mobile"): which block layouts apply
 * (set on <html data-hwdi-device>, read by the layout stylesheet).
 * options.designer ({ edit, selectedId, typeLabel }): editor preview only --
 * adds the visual editing script (selection frame, move, resize).
 */
export const buildSandboxDocument = (tutorial = {}, options = {}) => {
  const labels = { ...DEFAULT_LABELS, ...(options.labels || {}) };
  const htmlMode = hasTutorialHtmlCode(tutorial);
  const raw = htmlMode ? buildTutorialHtmlDocument(tutorial) : serializeHowWeDoItPage(tutorial);
  const device = LAYOUT_DEVICES.includes(options.device) ? options.device : "desktop";
  // Position / size / layer / spacing of the blocks (contentBlocks pages).
  const layoutCss = htmlMode ? "" : buildLayoutCss(tutorial.contentBlocks || []);
  const prepared = prepareSandboxMarkup(raw, labels);
  let page = prepared.html;
  const csp = buildFrameCsp({
    appOrigin: options.appOrigin || "",
    apiOrigin: options.apiOrigin || "",
    extraStyleOrigins: options.styleOrigins || [],
  });
  const appStyles = shouldUseAppStyles(tutorial) ? options.appStylesMarkup || "" : "";
  // Everything goes in ONE insertion right after the opening <head>: CSP
  // first, then the shim (it must run before the author's scripts and must
  // never be placed by searching for "<body" -- the cloned app CSS can
  // contain that text in a comment), then the base and app styles, before
  // the author's own <head> content so author CSS wins.
  const head = [
    `<meta http-equiv="Content-Security-Policy" content="${escapeHtml(csp)}" />`,
    buildShimScript(labels),
    `<style data-hwdi-frame-base>${FRAME_BASE_CSS}</style>`,
    appStyles,
    // After the app styles so the block layouts win over them; the author's
    // own <head> CSS still comes later.
    layoutCss ? `<style data-hwdi-layout>${layoutCss}</style>` : "",
    options.designer && !htmlMode
      ? buildDesignerScript({
          source: FRAME_MESSAGE_SOURCE,
          parentSource: PARENT_MESSAGE_SOURCE,
          edit: options.designer.edit !== false,
          selectedId: options.designer.selectedId,
          blocks: buildDesignerBlocks(tutorial.contentBlocks || [], device, options.designer.typeLabel, options.designer.unlockedIds),
        })
      : "",
  ].join("\n");

  const withHead = injectAfterOpeningTag(page, "head", head);
  if (withHead !== null) {
    page = withHead;
  } else {
    const withHtml = injectAfterOpeningTag(page, "html", `<head>\n${head}\n</head>`);
    page = withHtml !== null ? withHtml : `<!doctype html><html><head>${head}</head><body>${page}</body></html>`;
  }
  page = page.replace(/<html(?=[\s>])/i, `<html data-hwdi-device="${device}"`);
  return { html: page, slots: prepared.slots, blocked: prepared.blocked };
};

export const frameOrigins = (apiBase) => {
  const appOrigin = typeof window !== "undefined" ? window.location.origin : "";
  const apiOrigin = originOf(apiBase) || appOrigin;
  return { appOrigin, apiOrigin };
};

/** Absolute origins of the stylesheet links cloned from the app document. */
export const stylesheetOriginsFromMarkup = (markup = "") =>
  unique([...String(markup).matchAll(/<link[^>]+href=["']([^"']+)["']/gi)].map((match) => originOf(match[1])));
